/**
 * <product-bundle>
 *
 * Frequently-bought-together widget. Recalculates the bundle total on
 * checkbox change and adds every checked, in-stock product to the cart in a
 * single /cart/add.js request when submitted.
 *
 * Each product uses `selected_or_first_available_variant`, quantity 1. If a
 * bundle product has multiple variants, this always adds the first
 * available one — there is no in-widget variant picker yet.
 */
if (!customElements.get('product-bundle')) {
  customElements.define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      constructor() {
        super();

        this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.discountPercent = this.dataset.discountPercent || '0';
        this.bundleAddText = this.dataset.bundleAddText;
        this.singleAddText = this.dataset.singleAddText;
        this.soldOutError = this.dataset.soldOutError;
        this.genericError = this.dataset.genericError;

        this.originalEl = this.querySelector('[data-bundle-original]');
        this.finalEl = this.querySelector('[data-bundle-final]');
        this.badgeEl = this.querySelector('[data-bundle-badge]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitButtonText = this.querySelector('[data-bundle-submit-text]');
        this.spinner = this.querySelector('.loading__spinner');
        this.errorWrapper = this.querySelector('[data-bundle-error]');
        this.errorText = this.querySelector('[data-bundle-error-text]');

        this.items.forEach((item) => {
          const checkbox = item.querySelector('[data-bundle-checkbox]');
          checkbox?.addEventListener('change', this.update.bind(this));
        });
        this.submitButton?.addEventListener('click', this.onSubmit.bind(this));

        this.update();
      }

      get selectedItems() {
        return this.items.filter((item) => {
          const checkbox = item.querySelector('[data-bundle-checkbox]');
          return checkbox && checkbox.checked && !checkbox.disabled;
        });
      }

      update() {
        const selected = this.selectedItems;
        const count = selected.length;
        const isSingle = count <= 1;

        const originalCents = selected.reduce((sum, item) => sum + parseInt(item.dataset.price, 10), 0);
        const discountedCents = selected.reduce(
          (sum, item) => sum + parseInt(item.dataset.discountedPrice, 10),
          0
        );

        this.classList.toggle('is-single', isSingle);

        if (this.originalEl) this.originalEl.textContent = this.formatMoney(originalCents);
        if (this.badgeEl) this.badgeEl.textContent = `-${this.discountPercent}%`;
        if (this.finalEl) {
          this.finalEl.textContent = this.formatMoney(isSingle ? originalCents : discountedCents);
        }

        if (this.submitButtonText) {
          this.submitButtonText.textContent = isSingle ? this.singleAddText : this.bundleAddText;
        }

        const nothingSelected = count === 0;
        if (this.submitButton) {
          this.submitButton.disabled = nothingSelected;
          this.submitButton.setAttribute('aria-disabled', String(nothingSelected));
        }

        this.hideError();
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholderRegex);

        const formatWithDelimiters = (number, precision, thousands, decimal) => {
          number = (Math.round((number || 0)) / 100).toFixed(precision);
          const parts = number.split('.');
          const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
          const centsPart = parts[1] ? decimal + parts[1] : '';
          return dollars + centsPart;
        };

        let value;
        switch (match && match[1]) {
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'", '.');
            break;
          case 'amount_with_space_separator':
            value = formatWithDelimiters(cents, 2, ' ', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ', ',');
            break;
          case 'amount':
          default:
            value = formatWithDelimiters(cents, 2, ',', '.');
        }

        return match ? format.replace(placeholderRegex, value) : format + value;
      }

      showError(message) {
        if (!this.errorWrapper) return;
        if (this.errorText) this.errorText.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }

      hideError() {
        if (!this.errorWrapper) return;
        this.errorWrapper.setAttribute('hidden', 'hidden');
      }

      setLoading(loading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', String(loading));
        this.submitButton.disabled = loading;
        this.spinner?.classList.toggle('hidden', !loading);
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const selected = this.selectedItems;
        if (!selected.length) return;

        const unavailable = selected.some((item) => item.dataset.available === 'false');
        if (unavailable) {
          this.showError(this.soldOutError);
          this.update();
          return;
        }

        const items = selected.map((item) => ({
          id: parseInt(item.dataset.variantId, 10),
          quantity: 1,
        }));

        this.hideError();
        this.setLoading(true);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            throw new Error(data.description || data.message || this.genericError);
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-bundle',
              cartData: data,
            });
          }

          if (cart) {
            cart.renderContents(data);
          } else {
            window.location = window.routes.cart_url;
            return;
          }
        } catch (error) {
          this.showError(error.message || this.genericError);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
